import { Component, OnInit, OnDestroy, AfterViewInit, ElementRef, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { LocationService, Position } from '../../services/location.service';
import { MapsService, UnifiedPlace } from '../../services/maps.service';
import * as L from 'leaflet';

// Fix Leaflet default icon paths (broken by webpack)
const iconRetinaUrl = 'assets/marker-icon-2x.png';
const iconUrl = 'assets/marker-icon.png';
const shadowUrl = 'assets/marker-shadow.png';

@Component({
  selector: 'app-nearby-restaurants',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './nearby-restaurants.component.html',
  styleUrl: './nearby-restaurants.component.scss'
})
export class NearbyRestaurantsComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('mapContainer', { static: false }) mapContainer!: ElementRef;

  private map: L.Map | null = null;
  private userMarker: L.Marker | null = null;
  private restaurantMarkers: L.Marker[] = [];

  restaurants: UnifiedPlace[] = [];
  loading = false;
  locationError = false;
  userLat = 0;
  userLng = 0;
  mapReady = false;
  activeView: 'map' | 'list' = 'map';
  selectedPlace: UnifiedPlace | null = null;

  constructor(
    private locationService: LocationService,
    private mapsService: MapsService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.getCurrentLocation();
  }

  ngAfterViewInit(): void {
    // Map will be initialized after location is obtained
  }

  ngOnDestroy(): void {
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  getCurrentLocation(): void {
    this.loading = true;
    this.locationError = false;

    this.locationService.getCurrentPosition().subscribe({
      next: (position: Position) => {
        this.userLat = position.latitude;
        this.userLng = position.longitude;
        this.initMap();
        this.loadNearbyRestaurants();
      },
      error: () => {
        this.locationError = true;
        this.loading = false;
        // Default to a location so the map still shows
        this.userLat = 41.3851;
        this.userLng = 2.1734; // Barcelona
        this.initMap();
      }
    });
  }

  private initMap(): void {
    if (this.map || !this.mapContainer?.nativeElement) {
      // Retry after view renders
      setTimeout(() => {
        if (!this.map && this.mapContainer?.nativeElement) {
          this.createMap();
        }
      }, 100);
      return;
    }
    this.createMap();
  }

  private createMap(): void {
    if (this.map) return;

    this.map = L.map(this.mapContainer.nativeElement, {
      zoomControl: false,
      attributionControl: false,
    }).setView([this.userLat, this.userLng], 15);

    // Dark map tiles (matches BarClik brand)
    L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
      maxZoom: 19,
    }).addTo(this.map);

    // Zoom control bottom-right
    L.control.zoom({ position: 'bottomright' }).addTo(this.map);

    // User location marker (green pulsing dot)
    const userIcon = L.divIcon({
      className: 'user-location-marker',
      html: '<div class="user-dot"><div class="user-dot-pulse"></div></div>',
      iconSize: [20, 20],
      iconAnchor: [10, 10],
    });

    this.userMarker = L.marker([this.userLat, this.userLng], { icon: userIcon })
      .addTo(this.map)
      .bindPopup('<strong>Tu ubicacion</strong>');

    this.mapReady = true;

    // Invalidate size after render
    setTimeout(() => this.map?.invalidateSize(), 200);
  }

  private loadNearbyRestaurants(): void {
    this.mapsService.searchRestaurants({
      lat: this.userLat,
      lng: this.userLng,
      radius: 2000,
      limit: 20,
      useMapbox: true,
      useNominatim: true,
    }).subscribe({
      next: (places) => {
        this.restaurants = places;
        this.loading = false;
        this.addRestaurantMarkers();
      },
      error: () => {
        this.loading = false;
      }
    });
  }

  private addRestaurantMarkers(): void {
    if (!this.map) return;

    // Clear existing markers
    this.restaurantMarkers.forEach(m => m.remove());
    this.restaurantMarkers = [];

    for (const place of this.restaurants) {
      const icon = L.divIcon({
        className: 'restaurant-marker',
        html: `<div class="marker-pin">${this.getCategoryIcon(place.category)}</div>`,
        iconSize: [36, 36],
        iconAnchor: [18, 36],
        popupAnchor: [0, -36],
      });

      const marker = L.marker([place.location.lat, place.location.lng], { icon })
        .addTo(this.map)
        .bindPopup(`
          <div style="min-width:150px">
            <strong>${place.name}</strong><br>
            <small>${place.category || 'Restaurante'}</small><br>
            ${place.distance ? `<small>${this.formatDistance(place.distance)}</small>` : ''}
          </div>
        `);

      marker.on('click', () => {
        this.selectedPlace = place;
      });

      this.restaurantMarkers.push(marker);
    }

    // Fit bounds if we have markers
    if (this.restaurantMarkers.length > 0) {
      const group = L.featureGroup([this.userMarker!, ...this.restaurantMarkers]);
      this.map.fitBounds(group.getBounds().pad(0.1));
    }
  }

  setView(view: 'map' | 'list'): void {
    this.activeView = view;
    if (view === 'map') {
      setTimeout(() => this.map?.invalidateSize(), 100);
    }
  }

  centerOnUser(): void {
    if (this.map && this.userLat) {
      this.map.setView([this.userLat, this.userLng], 15);
    }
  }

  onRestaurantClick(place: UnifiedPlace): void {
    if (this.activeView === 'map' && this.map) {
      this.map.setView([place.location.lat, place.location.lng], 17);
      const marker = this.restaurantMarkers.find(m => {
        const pos = m.getLatLng();
        return Math.abs(pos.lat - place.location.lat) < 0.0001 &&
               Math.abs(pos.lng - place.location.lng) < 0.0001;
      });
      marker?.openPopup();
    }
    this.selectedPlace = place;
  }

  goToBusinessDetail(place: UnifiedPlace): void {
    this.router.navigate(['/business', place.id], {
      state: {
        businessData: {
          id: place.id,
          name: place.name,
          address: place.address,
          phone: place.phone || '',
          cuisineType: place.category || 'Restaurante',
          priceRange: '',
          rating: 0,
          reviewCount: 0,
          isOpen: true,
          description: '',
          specialOffers: [],
          hours: ''
        }
      }
    });
  }

  openDirections(place: UnifiedPlace): void {
    const url = `https://www.google.com/maps/dir/?api=1&destination=${place.location.lat},${place.location.lng}`;
    window.open(url, '_blank');
  }

  formatDistance(meters: number): string {
    if (meters < 1000) return `${Math.round(meters)} m`;
    return `${(meters / 1000).toFixed(1)} km`;
  }

  getCategoryIcon(category?: string): string {
    const cat = (category || '').toLowerCase();
    if (cat.includes('cafe') || cat.includes('coffee')) return '☕';
    if (cat.includes('bar') || cat.includes('pub')) return '🍺';
    if (cat.includes('bakery') || cat.includes('panaderia')) return '🥐';
    if (cat.includes('fast') || cat.includes('rapida')) return '🍔';
    return '🍽️';
  }

  goBack(): void {
    this.router.navigate(['/dashboard']);
  }
}
